import axios, { type AxiosInstance } from 'axios'

/**
 * Single axios instance for the Laravel API.
 *
 * Auth is Sanctum's SPA mode: the browser holds an httpOnly session cookie, so
 * no token is ever stored in JS. Call `initCsrf()` once before the first
 * mutating request (the login form does this).
 */
export const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL ?? '/api/v1',
  withCredentials: true,
  withXSRFToken: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
})

export function initCsrf(): Promise<unknown> {
  return axios.get('/sanctum/csrf-cookie', { withCredentials: true })
}

/** Set the active locale so the API returns translated labels and errors. */
export function setApiLocale(locale: 'en' | 'ar'): void {
  api.defaults.headers.common['Accept-Language'] = locale
}

export interface ApiError {
  status: number
  message: string
  errors: Record<string, string[]>
}

function toApiError(error: unknown): ApiError {
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as { message?: string; errors?: Record<string, string[]> } | undefined
    return {
      status: error.response?.status ?? 0,
      message: data?.message ?? error.message,
      errors: data?.errors ?? {},
    }
  }
  return { status: 0, message: 'Something went wrong. Please try again.', errors: {} }
}

api.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    const apiError = toApiError(error)

    // 401 means the session lapsed — bounce to login rather than showing a
    // half-empty screen. 403/404 are handled by the calling feature, because a
    // guest hitting another client's record must look identical to "not found".
    if (apiError.status === 401 && !window.location.pathname.startsWith('/login')) {
      window.location.assign('/login')
    }

    return Promise.reject(apiError)
  },
)
